import { Component, useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import clsx from "clsx";
import { ApiError, fetchDealInbox, refreshDealInbox, type SendResult } from "../../api";
import type { DealInboxResponse } from "../../types/email";
import { TabPanel } from "./TabPanel";
import { EmailDetailModal } from "../cockpit/EmailDetailModal";
import { EmailComposeModal } from "../email/EmailComposeModal";
import { EmailListRow } from "../email/EmailListRow";

/**
 * Crash boundary mirroring LME / Doc Portal — catches contract drift (Edge-side
 * type mismatch) and renders a clear pane instead of blanking Focus Mode.
 */
class EmailErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err: Error) {
    console.error("[EmailTab] render error:", err);
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

type Filter = "all" | "incoming" | "outgoing";

interface EmailTabProps {
  dealId: string;
}

export function EmailTab({ dealId }: EmailTabProps) {
  const [inbox, setInbox] = useState<DealInboxResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [sent, setSent] = useState<SendResult | null>(null);
  const [boundaryKey, setBoundaryKey] = useState(0);
  // QW-8: pre-address a new email to the deal's borrower (first known address).
  const borrowerEmail = inbox?.addresses?.[0];

  const load = useCallback(
    async (mode: "initial" | "manual") => {
      if (mode === "manual") {
        setRefreshing(true);
        // Bust the 5-min KV cache so manual refresh re-runs the match.
        try {
          await refreshDealInbox(dealId);
        } catch {
          // Non-fatal — fall through to a (possibly cached) fetch.
        }
      }
      try {
        const data = await fetchDealInbox(dealId);
        setInbox(data);
        setError(null);
      } catch (err) {
        setError(
          err instanceof ApiError
            ? `${err.code} (HTTP ${err.status})`
            : err instanceof Error
              ? err.message
              : "fetch_failed",
        );
      } finally {
        if (mode === "manual") setRefreshing(false);
        if (mode === "initial") setLoading(false);
      }
    },
    [dealId],
  );

  useEffect(() => {
    setLoading(true);
    setInbox(null);
    setError(null);
    setFilter("all");
    setQuery("");
    void load("initial");
  }, [dealId, load]);

  const filtered = useMemo(() => {
    if (!inbox) return [];
    const q = query.trim().toLowerCase();
    return inbox.emails.filter((e) => {
      if (filter !== "all" && e.direction !== filter) return false;
      if (!q) return true;
      return (
        e.subject.toLowerCase().includes(q) ||
        e.from.name.toLowerCase().includes(q) ||
        e.from.email.toLowerCase().includes(q)
      );
    });
  }, [inbox, filter, query]);

  if (loading) {
    return (
      <TabPanel>
        <div className="lme-loading">
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-label">MATCHING EMAILS</span>
        </div>
      </TabPanel>
    );
  }

  if (error) {
    return (
      <TabPanel>
        <div className="lme-error">
          ⚠ {error}
          <button
            className="lme-retry"
            onClick={() => {
              setError(null);
              setLoading(true);
              void load("initial");
            }}
          >
            RETRY
          </button>
        </div>
      </TabPanel>
    );
  }

  return (
    <EmailErrorBoundary
      key={`${dealId}:${boundaryKey}`}
      fallback={
        <TabPanel>
          <div className="lme-error">
            ⚠ EMAIL render error — likely contract drift. Refresh to retry.
            <button
              className="lme-retry"
              onClick={() => {
                setBoundaryKey((k) => k + 1);
                void load("manual");
              }}
            >
              RETRY
            </button>
          </div>
        </TabPanel>
      }
    >
      <TabPanel>
        <div className="email-tab">
          <div className="email-header">
            <div className="email-header-counts">
              <span className="email-header-total">
                {inbox?.emails.length ?? 0} EMAILS
              </span>
              <span className="email-header-meta">
                · {inbox?.thread_count ?? 0} threads
                {inbox?.cached ? " · cached" : ""}
              </span>
            </div>
            <div className="email-header-actions">
              <button
                className="email-compose-btn"
                onClick={() => setComposing(true)}
                title={
                  borrowerEmail
                    ? `Write a new email to ${borrowerEmail}`
                    : "Write a new email (no borrower address on file — add a recipient)"
                }
              >
                ✎ NEW EMAIL
              </button>
              <button
                className="lme-refresh"
                onClick={() => void load("manual")}
                disabled={refreshing}
                title="Re-match this deal's emails against Outlook"
              >
                {refreshing ? "↻ …" : "↻ REFRESH"}
              </button>
            </div>
          </div>

          {sent && (
            <div className="email-sent-banner">
              ✓ Email sent to {sent.sent_to.join(", ")}.
            </div>
          )}

          {inbox?.capped && (
            <div className="email-banner email-banner-warn">
              Showing the 25 most recent threads — this deal has more. Older
              threads are hidden.
            </div>
          )}

          <div className="email-controls">
            <div className="email-filter-pills">
              {(["all", "incoming", "outgoing"] as Filter[]).map((f) => (
                <button
                  key={f}
                  className={clsx(
                    "email-filter-pill",
                    filter === f && "active",
                  )}
                  onClick={() => setFilter(f)}
                >
                  {f.toUpperCase()}
                </button>
              ))}
            </div>
            <input
              className="email-search"
              type="search"
              placeholder="Search subject / sender…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="email-list">
            {filtered.length === 0 ? (
              <div className="email-empty">
                {inbox && inbox.emails.length === 0
                  ? "No matched emails for this deal yet. Emails appear once a deal contact is on the thread."
                  : "No emails match this filter."}
              </div>
            ) : (
              filtered.map((e) => (
                <EmailListRow
                  key={e.id}
                  email={e}
                  onClick={() => setSelected(e.id)}
                />
              ))
            )}
          </div>

          {inbox?.limitations.includes("no_known_contact_addresses") && (
            <div className="email-limitation-hint">
              ⚠ No borrower email on file for this deal — nothing to match. Add
              the borrower’s email to their Zoho contact.
            </div>
          )}
          {inbox?.limitations.includes(
            "co_borrower_emails_only_via_thread_expansion",
          ) && (
            <div className="email-limitation-hint">
              ⓘ Co-borrower emails appear only when they share a thread with a
              known contact.
            </div>
          )}
        </div>

        {selected && (
          <EmailDetailModal
            messageId={selected}
            dealId={dealId}
            onClose={() => setSelected(null)}
            // Re-match this deal's inbox when a reply is sent or the thread's
            // deal link changes from the banner — so a re-pinned-away email
            // drops out instead of lingering as a ghost row.
            onMutated={() => void load("manual")}
          />
        )}

        {composing && (
          <EmailComposeModal
            // EM-1: scope "From docs" to this deal (also enables the button).
            dealId={dealId}
            // QW-8: pre-address to the deal's borrower (if we have an address).
            initialTo={borrowerEmail ? [{ email: borrowerEmail }] : undefined}
            onClose={() => setComposing(false)}
            onSent={(result) => {
              setSent(result);
              setComposing(false);
            }}
          />
        )}
      </TabPanel>
    </EmailErrorBoundary>
  );
}
